import React, { useState } from 'react';
import {
  AppBar,
  Typography,
  Toolbar,
  IconButton,
  InputBase,
  Box,
  alpha,
  styled,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import LogoutIcon from '@mui/icons-material/Logout';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAuth } from '../../../hooks/useAuth';
import imgUrl from '../../../assets/images/imgUrl';

// Custom styled search bar
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 50,
  backgroundColor: alpha(theme.palette.common.black, 0.05),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.1),
  },
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.grey[600]
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  paddingLeft: `calc(1em + ${theme.spacing(4)})`,
  width: '100%',
  [theme.breakpoints.up('md')]: {
    width: '30ch',
  },
}));

const DashboardHeader = ({ onDrawerToggle, onGoToInvitation, isGroupChatActive, isOneToOneChatActive, onBackToCategories, onBackToUserList, selectedGroup, selectedUser }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Generate referral link based on usertype and temp_peacekeeper_id
  const generateReferralLink = () => {
    const domain = window.location.origin;
    const peacekeeperId = user?.peacekeeper_id || 'IN-0000000-PK';

    // Priority 1: For Entity usertype with categoryname
    if (user?.usertype === 'Entity' && user?.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    if (user?.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users
    return `${domain}/register?referencenumber=${peacekeeperId}`;
  };

  const handleShareWhatsApp = () => {
    const referralLink = generateReferralLink();
    const message = `I am a Peacekeeper! Name: ${user?.username || user?.name || 'User'}, ID: ${user?.peacekeeper_id || 'IN-0000000-PK'}. Join the movement: ${referralLink}`;

    // Try Cordova social sharing with attachment if available
    if (window.cordova && window.cordova.plugins && window.cordova.plugins.socialsharing) {
      // For header sharing, we'll share the message and let user know they can get the certificate
      const enhancedMessage = `${message}\n\n📱 Get your certificate with attachment from the dashboard!`;

      // Try to share directly to WhatsApp
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        try {
          window.cordova.plugins.socialsharing.shareToWhatsApp(
            enhancedMessage,
            null, // no file for header sharing
            null,
            (success) => {
              console.log('Shared to WhatsApp successfully');
            },
            (error) => {
              console.log('WhatsApp sharing failed, opening WhatsApp Web');
              openWhatsAppWeb(enhancedMessage);
            }
          );
          return;
        } catch (error) {
          console.log('Cordova WhatsApp sharing failed:', error);
        }
      }
    }

    // Fallback: Open WhatsApp Web
    openWhatsAppWeb(message);
  };

  const openWhatsAppWeb = (message) => {
    // Use Cordova InAppBrowser if available for better mobile experience
    if (window.cordova && window.cordova.InAppBrowser) {
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.cordova.InAppBrowser.open(whatsappUrl, '_blank', 'location=yes');
    } else {
      // Fallback to regular window.open
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  return (
    <AppBar
  position="fixed"
  color="inherit"
  elevation={1}
  sx={{
    zIndex: (theme) => theme.zIndex.drawer + 1,
  }}
  className="mainHeaderSection"
>
  <Toolbar sx={{ justifyContent: 'space-between', position: 'relative' }}> {/* 🔥 Add position: relative */}
    
    {/* Left Section */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
      {isGroupChatActive ? (
        <IconButton edge="start" color="inherit" onClick={onBackToCategories} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
      ) : (
        <Box 
          onClick={onDrawerToggle} 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            cursor: 'pointer',
            color: 'inherit',
            mr: 1
          }}
        >
          <IconButton color="inherit" sx={{ p: 0.5 }}>
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '11px', 
              fontWeight: 600,
              mt: -0.5,
              color: 'inherit',
              letterSpacing: '0.5px'
            }}
          >
            Menu
          </Typography>
        </Box>
      )}

      {/* Logo container - responsive center on mobile */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          position: { xs: 'absolute', sm: 'static' }, // 🔥 absolute on mobile, normal on larger screens
          left: { xs: '50%', sm: 'auto' },             // 🔥 center horizontally on mobile
          transform: { xs: 'translateX(-50%)', sm: 'none' }, // 🔥 perfect center on mobile
        }}
      >
        <img src={imgUrl.peaceMovImg} className="dashboardPeaceLogo" />
        
        {/* Group Chat Header Info */}
        {/* {isGroupChatActive && selectedGroup && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedGroup.icon}
            <Box>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                sx={{ fontSize: '1rem' }}
              >
                {selectedGroup.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedGroup.memberCount} members
              </Typography>
            </Box>
          </Box>
        )} */}
      </Box>
    </Box>

    {/* Right Icons */}
    <Box sx={{ display: 'flex', gap: 2 }} className="headerMenuIcons">
      <Box 
        onClick={handleLogout} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          cursor: 'pointer',
          color: 'inherit'
        }}
      >
        <IconButton color="inherit" sx={iconStyle}>
          <LogoutIcon />
        </IconButton>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '11px', 
            fontWeight: 600,
            mt: -0.5,
            color: 'inherit',
            letterSpacing: '0.5px'
          }}
        >
          Logout
        </Typography>
      </Box>
    </Box>
  </Toolbar>
</AppBar>

  );
};

const iconStyle = {
  border: '1px solid #0B8270',
  borderRadius: '50%',
  padding: 0.75,
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#e0e0e0',
  },
};

export default DashboardHeader;
